import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palmtree, Loader2, Building2, Wrench } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Cleaning", "Handyman", "Lawn & Landscaping", "Pool Maintenance", "Concrete", "HVAC", "General Contractor", "Electrical", "Plumbing", "Pest Control"];
const routeFor = (role) => (role === "admin" ? "/admin" : role === "pm" ? "/pm" : "/vendor");

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "vendor" ? "vendor" : "pm");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", phone: "" });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleCat = (c) => setCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({ ...form, role, service_categories: role === "vendor" ? cats : undefined });
      toast.success("Account created!");
      navigate(routeFor(u.role));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0B192C] grain">
      <div className="w-full max-w-lg bg-[#F8FAFC] rounded-2xl p-6 sm:p-8 relative z-10">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-sunburst flex items-center justify-center"><Palmtree className="w-5 h-5 text-white" /></div>
          <span className="font-display font-bold text-slate-900">Florida Property Assistants</span>
        </Link>
        <h1 className="mt-6 text-2xl font-display font-bold text-slate-900">Create your account</h1>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button type="button" data-testid="register-role-pm" onClick={() => setRole("pm")}
            className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-semibold transition-all ${role === "pm" ? "border-sunburst bg-orange-50 text-slate-900" : "border-slate-200 bg-white text-slate-500"}`}>
            <Building2 className="w-4 h-4" /> Property Manager
          </button>
          <button type="button" data-testid="register-role-vendor" onClick={() => setRole("vendor")}
            className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-semibold transition-all ${role === "vendor" ? "border-sunburst bg-orange-50 text-slate-900" : "border-slate-200 bg-white text-slate-500"}`}>
            <Wrench className="w-4 h-4" /> Service Vendor
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>{role === "pm" ? "Company" : "Business name"}</Label>
              <Input data-testid="register-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 h-11" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11" />
            </div>
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11" />
          </div>
          {role === "vendor" && (
            <div>
              <Label>Service categories</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c} onClick={() => toggleCat(c)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${cats.includes(c) ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-500 border-slate-200"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="register-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-sunburst hover:bg-orange-600 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Already registered? <Link to="/login" className="text-ocean font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
